import { currentPlanName, FEE_FALLBACK, platformFeeLabel } from "./accountSetupCopy";

export const PLAN_SWITCH_ERRORS = Object.freeze({
  TOO_EARLY: "You can switch to Free after {date}. Paid plans stay active for at least 1 year.",
  ALREADY_FREE: "This club is already on Free.",
  ALREADY_PAID: "This club already has a Paid plan.",
  PERIOD_END_SET: "Free is already scheduled for the end of this billing period.",
  NOT_FOUND: "This club does not have an active plan to switch.",
});

export function freeFeePhrase(fee) {
  if (fee?.displayLabel) {
    return platformFeeLabel(fee, FEE_FALLBACK.FREE);
  }
  if (fee?.feePercent != null && fee.feePercent !== "") {
    return `${fee.feePercent}%`;
  }
  return platformFeeLabel(fee, FEE_FALLBACK.FREE);
}

export function isFreeClubPlan({ freePlan, subscription, selectedClub }) {
  if (freePlan === true) {
    return true;
  }
  if (freePlan === false) {
    return false;
  }
  if (selectedClub?.freePlanUser === true) {
    return true;
  }
  return currentPlanName(subscription, selectedClub).toLowerCase() === "free";
}

export function formatSwitchDate(value) {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "number") {
    const fromEpoch = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(fromEpoch.getTime()) ? "" : fromEpoch.toISOString().slice(0, 10);
  }
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(text) || /^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? text : parsed.toISOString().slice(0, 10);
  }
  return text;
}

export function planSwitchDates(subscription = {}, setup = {}) {
  return {
    availableOn: formatSwitchDate(subscription.freeSwitchAvailableOn || setup.freeSwitchAvailableOn),
    periodEnd: formatSwitchDate(
      subscription.currentPeriodEnd || subscription.periodEnd || setup.currentPeriodEnd || setup.periodEnd
    ),
    pendingSwitch: subscription.cancelAtPeriodEnd === true || setup.cancelAtPeriodEnd === true,
    canSwitchToFree: subscription.canSwitchToFree === true || setup.canSwitchToFree === true,
  };
}

export function freePlanSwitchCopy(feeLabel) {
  const fee = feeLabel || FEE_FALLBACK.FREE;
  return {
    title: "SportMingle Free",
    body: `No monthly SportMingle fee. SportMingle takes ${fee} on card payments. You can upgrade to Paid anytime.`,
    button: "Upgrade to Paid",
    disabled: false,
    tooltip: "",
    pending: "",
    paidThrough: "",
  };
}

export function paidPlanSwitchCopy({
  availableOn = "",
  periodEnd = "",
  pendingSwitch = false,
  canSwitchToFree = false,
} = {}) {
  const baseBody = "You pay SportMingle for this plan. There is no extra SportMingle take on club card payments.";
  const paidThrough = periodEnd ? `Paid through ${periodEnd}` : "";
  if (pendingSwitch) {
    const until = periodEnd || "the end of this billing period";
    return {
      title: "Paid Plan",
      body: baseBody,
      pending: `Your Paid plan stays active until ${until}. After that you will be on Free. Card payments will then include the SportMingle Free Plan fee.`,
      button: "",
      disabled: true,
      tooltip: "",
      paidThrough,
    };
  }
  if (canSwitchToFree === true) {
    return {
      title: "Paid Plan",
      body: `${baseBody} Switch to Free takes effect at the end of your current billing period. You will not get a refund.`,
      pending: "",
      button: "Switch to Free",
      disabled: false,
      tooltip: "",
      paidThrough,
    };
  }
  if (availableOn) {
    return {
      title: "Paid Plan",
      body: `${baseBody} Switch to Free is available on ${availableOn}. Paid plans stay active for at least 1 year.`,
      pending: "",
      button: "Switch to Free",
      disabled: true,
      tooltip: `You can switch to Free after ${availableOn}. Unused Paid time is not refunded.`,
      paidThrough,
    };
  }
  return {
    title: "Paid Plan",
    body: `${baseBody} Switch to Free is available after 1 year on Paid. Paid plans stay active for at least 1 year.`,
    pending: "",
    button: "Switch to Free",
    disabled: true,
    tooltip: "You can switch to Free after your first year on Paid. Unused Paid time is not refunded.",
    paidThrough,
  };
}

export const UPGRADE_CONFIRM =
  "Upgrade to Paid now. SportMingle will stop taking the Free Plan fee on card payments after this plan is active. If you switch back later, you must stay Paid for 1 year, and Free starts at the end of a billing period. No refunds.";

export function switchToFreeConfirmCopy(periodEnd) {
  if (periodEnd) {
    return `Switch to Free at the end of this billing period (${periodEnd}). You keep Paid benefits until then. Unused time is not refunded. After ${periodEnd}, SportMingle’s Free Plan fee applies to card payments.`;
  }
  return "Switch to Free at the end of this billing period. You keep Paid benefits until then. Unused time is not refunded. After that, SportMingle’s Free Plan fee applies to card payments.";
}

export function planSwitchErrorMessage(code, { date } = {}) {
  if (code === "TOO_EARLY") {
    return PLAN_SWITCH_ERRORS.TOO_EARLY.replaceAll("{date}", date || "the 1-year date");
  }
  return PLAN_SWITCH_ERRORS[code] || "";
}

export function switchApiErrorMessage(error) {
  const data = error?.response?.data;
  const candidates = [data?.Message, data?.detail, data?.message, data?.title];
  for (const message of candidates) {
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }
  if (typeof data?.Data === "string" && data.Data.trim()) {
    return data.Data.trim();
  }
  return "Please contact support";
}

export function isPaidSubscriptionName(name) {
  return Boolean(name) && String(name).toLowerCase() !== "free";
}

export function catalogFreePlan(products = []) {
  return products.find((plan) => String(plan?.productName || "").toLowerCase() === "free") || null;
}

export function catalogPaidPlan(products = []) {
  return products.find((plan) => String(plan?.productName || "").toLowerCase() !== "free") || null;
}

export function upgradeToPaidSwitchBody({ userId, clubId, plan, isYear = false, subQuantity = 1 } = {}) {
  const quantity = Number(subQuantity) || 1;
  const unitAmount = Number(isYear ? plan?.yearlyUnitAmount : plan?.monthlyUnitAmount) || 0;
  return {
    userId,
    clubId,
    planId: plan?.planId,
    priceId: isYear ? plan?.yearlyPriceId : plan?.monthlyPriceId,
    amount: String(unitAmount * quantity),
    subQuantity: String(quantity),
    freePlan: false,
  };
}

export function switchToFreeBody({ userId, clubId, freePlanId } = {}) {
  return {
    userId,
    clubId,
    planId: freePlanId,
    freePlan: true,
  };
}

export function dollarsFromCents(cents) {
  const n = Number(cents);
  return Number.isFinite(n) ? (n / 100).toFixed(2) : "0.00";
}

export function formatCheckoutMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

export function platformUpgradeCheckoutState({ switchBody, responseData, clubId, returnTo = "/plan-setup" } = {}) {
  return {
    clientSecretKey: responseData?.clientSecretKey,
    paymentIntentId: responseData?.paymentIntentId,
    totalAmount: responseData?.totalAmount ?? dollarsFromCents(switchBody?.amount),
    processingFee: responseData?.processingFee ?? "0",
    membershipAmount: switchBody?.amount,
    clubId,
    pollUntilPaid: true,
    platformCheckout: true,
    returnTo,
  };
}

export async function pollUntilPaidSubscription(findFn, { intervalMs = 2000, attempts = 15, wait } = {}) {
  const sleep = wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
  let lastResponse = null;
  for (let i = 0; i < attempts; i += 1) {
    lastResponse = await findFn();
    if (isPaidSubscriptionName(lastResponse?.Data?.subscriptionName)) {
      return lastResponse;
    }
    if (i < attempts - 1) {
      await sleep(intervalMs);
    }
  }
  return lastResponse;
}
